/**
 * useUserPreferences - Hook para preferências do usuário logado
 *
 * Encapsula as RPCs `get_user_preferences` e `set_user_preferences`.
 * Retorna todas as preferências suportadas em um único shape:
 *   - default_task_category_id (categoria padrão do Kanban de Tarefas)
 *   - kanban_view_conversations ('tabs' | 'select') — formato de categorias
 *     no Kanban de Conversas. Default 'tabs' quando NULL.
 *   - kanban_view_tasks ('tabs' | 'select') — idem para o Kanban de Tarefas.
 *
 * @version 1.1.0
 * RELEASE: 2026-06-12 v1.0.0 - Criação inicial (apenas categoria padrão de tarefas)
 * RELEASE: 2026-07-07 v1.1.0 - Adicionadas preferências kanban_view_conversations
 *   e kanban_view_tasks. Introduzido `useSetUserPreferences` genérico e mantida
 *   compatibilidade com `useSetDefaultTaskCategory` (agora encaminha ao genérico).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Logger condicional do módulo
const log = createLogger('useUserPreferences');

/** Formato de visualização das categorias em um Kanban */
export type KanbanCategoryView = 'tabs' | 'select';

/** Shape das preferências do usuário */
export interface UserPreferences {
  /** Categoria padrão do Kanban de Tarefas (null = primeira disponível) */
  default_task_category_id: string | null;
  /** Formato do Kanban de Conversas (default 'tabs') */
  kanban_view_conversations: KanbanCategoryView;
  /** Formato do Kanban de Tarefas (default 'tabs') */
  kanban_view_tasks: KanbanCategoryView;
}

/**
 * Query: busca preferências do usuário autenticado.
 * Cache compartilhado por user id; staleTime alto pois muda raramente.
 */
export function useUserPreferences() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['user-preferences', user?.id],
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<UserPreferences> => {
      const { data, error } = await (supabase.rpc as any)('get_user_preferences');
      if (error) {
        log.error('Erro ao carregar preferências do usuário', { error });
        throw error;
      }
      // RPC retorna TABLE (1 linha) → pega o primeiro item
      const row = Array.isArray(data) ? data[0] : data;
      const viewConv = (row?.kanban_view_conversations as KanbanCategoryView | null) ?? 'tabs';
      const viewTasks = (row?.kanban_view_tasks as KanbanCategoryView | null) ?? 'tabs';
      return {
        default_task_category_id: row?.default_task_category_id ?? null,
        kanban_view_conversations: viewConv === 'select' ? 'select' : 'tabs',
        kanban_view_tasks: viewTasks === 'select' ? 'select' : 'tabs',
      };
    },
  });
}

/** Payload aceito por `set_user_preferences` — todas as chaves são opcionais */
export type SetUserPreferencesInput = Partial<{
  default_task_category_id: string | null;
  kanban_view_conversations: KanbanCategoryView | null;
  kanban_view_tasks: KanbanCategoryView | null;
}>;

/**
 * Mutation genérica: salva qualquer subconjunto de preferências do usuário.
 * Só envia ao backend as chaves presentes no input (permite null explícito
 * para limpar). Server-side valida FK/enum e registra bypass attempts.
 */
export function useSetUserPreferences() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (prefs: SetUserPreferencesInput) => {
      const { error } = await (supabase.rpc as any)('set_user_preferences', {
        p_prefs: prefs,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-preferences', user?.id] });
      toast.success('Preferência salva');
    },
    onError: (err: Error) => {
      log.error('Erro ao salvar preferências', { error: err });
      toast.error(`Erro ao salvar preferência: ${err.message}`);
    },
  });
}

/**
 * Mutation legada: define a categoria padrão do Kanban de Tarefas.
 * Mantida por compatibilidade — internamente delega para `useSetUserPreferences`.
 */
export function useSetDefaultTaskCategory() {
  const setPrefs = useSetUserPreferences();
  return {
    ...setPrefs,
    mutate: (categoryId: string | null) =>
      setPrefs.mutate({ default_task_category_id: categoryId }),
    mutateAsync: (categoryId: string | null) =>
      setPrefs.mutateAsync({ default_task_category_id: categoryId }),
  };
}
